import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { PawWatermark } from "./Paws";
import { reviews } from "../data/siteConfig";
import { reviewsPendingToast } from "../lib/notify";
import { Star, PawPrint, ExternalLink } from "lucide-react";

const Stars = () => (
    <div className="flex gap-1" aria-label="Five stars">
        {[...Array(5)].map((_, i) => (
            <Star
                key={i}
                className="h-5 w-5 fill-honey text-honey"
                aria-hidden="true"
            />
        ))}
    </div>
);

export const Reviews = () => (
    <section
        className="relative overflow-hidden bg-cream py-24 text-[#1c1917] sm:py-32"
        data-testid="reviews-section"
    >
        <PawWatermark tone="light" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <Chapter
                no="06"
                id="reviews"
                eyebrow="Reviews"
                title={
                    <>
                        In their words — <em>and their cats'.</em>
                    </>
                }
            />

            <div className="mt-16 grid gap-5 lg:grid-cols-12">
                {/* Google rating crest */}
                <Reveal className="lg:col-span-4">
                    <div
                        className="flex h-full flex-col justify-between rounded-3xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10"
                        data-testid="google-rating-card"
                    >
                        <div>
                            <p className="font-serif text-4xl font-medium leading-tight text-[#1c1917] sm:text-5xl">
                                Our Five-Star Google Reviews
                            </p>
                            <div className="mt-4" aria-hidden="true">
                                <Stars />
                            </div>
                        </div>
                        {reviews.googleUrl ? (
                            <a
                                href={reviews.googleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-8 inline-flex items-center gap-2 self-start text-sm font-semibold text-fern transition-colors duration-200 hover:text-[#1c1917]"
                                data-testid="google-reviews-link"
                            >
                                Read our Google reviews
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        ) : (
                            <button
                                onClick={reviewsPendingToast}
                                className="mt-8 inline-flex items-center gap-2 self-start text-sm font-semibold text-fern transition-colors duration-200 hover:text-[#1c1917]"
                                data-testid="google-reviews-link"
                            >
                                Read our Google reviews
                                <ExternalLink className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </Reveal>

                {/* Customer phrases */}
                <div className="grid gap-5 sm:grid-cols-3 lg:col-span-8">
                    {reviews.customerPhrases.map((phrase, i) => (
                        <Reveal key={phrase} delay={0.08 * i} className="h-full">
                            <figure
                                className="flex h-full flex-col justify-between rounded-3xl border border-stone-200 bg-white p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-fern/50 hover:shadow-md"
                                data-testid={`customer-phrase-${i}`}
                            >
                                <blockquote className="font-serif text-2xl font-medium italic leading-snug text-[#1c1917] sm:text-3xl">
                                    “{phrase}”
                                </blockquote>
                                <figcaption className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                                    How our customers describe us
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>

            {/* Featured genuine reviews */}
            <div className="mt-14">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone-500">
                        Featured reviews
                    </p>
                </Reveal>
                <div className="mt-6 grid gap-5 md:grid-cols-3">
                    {reviews.testimonials.length > 0
                        ? reviews.testimonials.map((t, i) => (
                              <Reveal key={t.name} delay={0.06 * i} className="h-full">
                                  <figure
                                      className="flex h-full flex-col rounded-3xl border border-stone-200 bg-white p-7"
                                      data-testid={`testimonial-${i}`}
                                  >
                                      <Stars />
                                      <blockquote className="mt-4 text-sm leading-relaxed text-stone-700 sm:text-base">
                                          “{t.text}”
                                      </blockquote>
                                      <figcaption className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-fern">
                                          {t.name} — Google review
                                      </figcaption>
                                  </figure>
                              </Reveal>
                          ))
                        : [0, 1, 2].map((i) => (
                              <Reveal key={i} delay={0.06 * i} className="h-full">
                                  <div
                                      className="flex h-full min-h-44 flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-stone-300 bg-white p-7 text-center"
                                      data-testid={`testimonial-placeholder-${i}`}
                                  >
                                      <PawPrint
                                          className="h-6 w-6 text-stone-300"
                                          aria-hidden="true"
                                      />
                                      <p className="max-w-56 text-xs leading-relaxed text-stone-400">
                                          A genuine Google review will be
                                          featured here — being selected by the
                                          family.
                                      </p>
                                  </div>
                              </Reveal>
                          ))}
                </div>
            </div>
        </div>
    </section>
);
